import { useEffect, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { googleAdsService } from '@/services/google-ads.service';
import { ChevronRight, Check, Loader2, Info, FlaskConical, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

type Reachable = { customerId: string; name: string; currency?: string; isManager: boolean; isTest: boolean };
type Client = Reachable & { level: number; status?: string };

/**
 * Two-level Google Ads customer picker.
 *
 * `listAccessibleCustomers` only returns what the OAuth login reaches
 * DIRECTLY — for the normal setup (a client account created inside an MCC)
 * that's just the manager. So a manager row expands into its `customer_client`
 * tree and the client is chosen from there, with login-customer-id set to the
 * manager automatically.
 */
export function AttachCustomerDialog({
  open,
  onClose,
  accountUid,
  onAttached,
}: {
  open: boolean;
  onClose: () => void;
  accountUid: string;
  onAttached: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [reachable, setReachable] = useState<Reachable[] | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [manager, setManager] = useState<Reachable | null>(null);
  const [clients, setClients] = useState<Client[] | null>(null);
  const [manualId, setManualId] = useState('');
  const [manualLogin, setManualLogin] = useState('');
  const [busy, setBusy] = useState(false);
  // Why the list came back empty — silently showing a bare ID box gives the
  // operator nothing to act on.
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !accountUid) return;
    let cancelled = false;
    setLoading(true);
    setReachable(null); setManualId(''); setManualLogin('');
    setManager(null); setClients(null); setWarning(null); setLoadError(null);
    googleAdsService.listAccessibleCustomers(accountUid)
      .then((r) => {
        if (cancelled) return;
        setWarning(r.warning ?? null);
        setReachable(r.customers ?? r.customerIds.map((id) => ({
          customerId: id, name: `Account ${id}`, isManager: false, isTest: false,
        })));
      })
      .catch((e) => {
        if (cancelled) return;
        setReachable([]);
        setLoadError(e?.response?.data?.message ?? e?.message ?? 'Could not read your Google Ads accounts.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, accountUid]);

  const openManager = async (m: Reachable) => {
    setManager(m);
    setClients(null);
    try {
      const r = await googleAdsService.listManagerClients(accountUid, m.customerId);
      setClients(r.clients);
    } catch {
      setClients([]);   // interceptor toasts the reason
    }
  };

  const attach = async (customerId: string, loginCustomerId?: string) => {
    setBusy(true);
    try {
      const acc = await googleAdsService.attachCustomer(accountUid, customerId, loginCustomerId);
      toast.success(acc?.currencyCode ? `Google Ads account connected — bills in ${acc.currencyCode}` : `Attached ${customerId}`);
      onAttached();
      onClose();
    } catch {
      /* interceptor toasts */
    } finally { setBusy(false); }
  };

  const Row = ({
    id, name, currency, isManager, isTest, status, onPick,
  }: {
    id: string; name: string; currency?: string; isManager: boolean; isTest: boolean;
    status?: string; onPick: () => void;
  }) => (
    <button
      onClick={onPick}
      disabled={busy}
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-3.5 py-3 text-left transition-colors hover:border-primary hover:bg-surface-alt disabled:opacity-60"
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-text-primary">
            {name}{isManager ? ' (manager)' : ''}
          </span>
          {isTest && (
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-warning-soft px-1.5 py-0.5 text-2xs font-semibold text-warning">
              <FlaskConical size={9} /> Test
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-2xs text-text-muted">
          ID {id}
          {currency ? ` · ${currency}` : ''}
          {status && status !== 'ENABLED' ? ` · ${status}` : ''}
          {isManager ? ' · click to see its accounts' : ''}
        </span>
      </span>
      {isManager
        ? <ChevronRight size={15} className="shrink-0 text-text-muted" />
        : <Check size={15} className="shrink-0 text-primary" />}
    </button>
  );

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="Choose a Google Ads account" widthClass="max-w-lg">
      <div className="space-y-3">
        {warning && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft/60 px-3 py-2.5">
            <Info size={13} className="mt-0.5 shrink-0 text-warning" />
            <p className="text-xs leading-relaxed text-text-secondary">{warning}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-text-muted">
            <Loader2 size={15} className="animate-spin" /> Reading your Google Ads accounts…
          </div>
        ) : manager ? (
          <>
            <button onClick={() => { setManager(null); setClients(null); }} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              <ArrowLeft size={12} /> All accounts
            </button>
            <p className="text-xs text-text-muted">
              Accounts under <strong className="text-text-primary">{manager.name}</strong>:
            </p>

            {clients === null ? (
              <div className="flex items-center gap-2 py-6 text-sm text-text-muted">
                <Loader2 size={15} className="animate-spin" /> Loading client accounts…
              </div>
            ) : clients.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-4 text-center text-xs text-text-muted">
                No client accounts found. Choose a different manager or create a client advertising account in Google Ads first.
              </p>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto">
                {clients.map((c) => (
                  <Row
                    key={c.customerId}
                    id={c.customerId}
                    name={c.name}
                    currency={c.currency}
                    isManager={c.isManager}
                    isTest={c.isTest}
                    status={c.status}
                    // login-customer-id must be the manager for a client read.
                    onPick={() => c.isManager ? openManager(c) : attach(c.customerId, manager.customerId)}
                  />
                ))}
              </div>
            )}

          </>
        ) : (
          <>
            <p className="text-xs leading-relaxed text-text-muted">
              Your Google login can access these accounts. Campaigns will be created under the one
              you pick — <strong className="text-text-secondary">choose an ad account, not a manager (MCC)</strong>.
            </p>
            {(reachable ?? []).length === 0 ? (
              <>
                <div className="rounded-lg border border-warning/40 bg-warning-soft/50 px-3 py-2.5">
                  <p className="text-xs font-semibold text-text-primary">
                    Google returned no accounts for this login
                  </p>
                  {loadError && <p className="mt-1 text-2xs text-danger">{loadError}</p>}
                  <ul className="mt-1.5 space-y-0.5 text-2xs leading-relaxed text-text-secondary">
                    <li>· Ask your administrator to check Google Ads API access.</li>
                    <li>· The signed-in Google user must have access to the Ads account.</li>
                  </ul>
                  <p className="mt-1.5 text-2xs text-text-muted">
                    You can still attach a customer ID by hand below.
                  </p>
                </div>
                <FormField label="Customer ID" required hintBelow hint="10 digits, no dashes — from the top-right of Google Ads.">
                  <Input value={manualId} onChange={(e) => setManualId(e.target.value)} placeholder="1197986398" />
                </FormField>
                <FormField label="Manager / login customer ID" hintBelow hint="Required when the account above sits inside an MCC — e.g. a sandbox client under a test manager.">
                  <Input value={manualLogin} onChange={(e) => setManualLogin(e.target.value)} placeholder="5695045742" />
                </FormField>
              </>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto">
                {(reachable ?? []).map((r) => (
                  <Row
                    key={r.customerId}
                    id={r.customerId}
                    name={r.name}
                    currency={r.currency}
                    isManager={r.isManager}
                    isTest={r.isTest}
                    onPick={() => (r.isManager ? openManager(r) : attach(r.customerId))}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <a href="/google-ads/account" className="text-xs text-primary hover:underline">
            Reconnect with a different Google login
          </a>
          <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          {!loading && !manager && (reachable ?? []).length === 0 && (
            <Button
              variant="primary"
              disabled={busy || manualId.replace(/\D+/g, '').length !== 10 || (!!manualLogin && manualLogin.replace(/\D+/g, '').length !== 10)}
              onClick={() => attach(manualId.replace(/\D+/g, ''), manualLogin.replace(/\D+/g, '') || undefined)}
            >
              Attach
            </Button>
          )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
